// src/documents/exportBriefing.js

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import grupoLogo from '../assets/grupo_logo.png';

/**
 * Loads and adds the company logo to the workbook
 */
const loadLogo = async (workbook) => {
    try {
        const logoResponse = await fetch(grupoLogo);
        if (!logoResponse.ok) {
            throw new Error(`Falha ao carregar o logo (status ${logoResponse.status}).`);
        }

        const blob = await logoResponse.blob();
        const mime = blob.type || 'image/png';
        let ext = mime.split('/').pop().toLowerCase() || 'png';

        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        const supportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
        if (!supportedExtensions.includes(ext)) {
            throw new Error(`Formato de imagem não suportado: "${ext}". Converta o logo para PNG ou JPEG.`);
        }

        if (ext === 'jpeg') ext = 'jpg';

        const logoImageId = workbook.addImage({ base64, extension: ext });

        if (logoImageId === undefined || logoImageId === null) {
            throw new Error('Logo não pôde ser adicionado à planilha.');
        }

        return logoImageId;
    } catch (error) {
        console.error('Logo loading error:', error);
        return null;
    }
};

/**
 * Adds Approval/Rejection checkboxes using simple bordered cells
 */
const addApprovalRejectionCheckboxes = (ws, project) => {
    try {
        // Position at the top-right area
        // Adjust these values based on your template layout
        const startRow = 2; // Row 2
        const startCol = 8; // Column H (8th column)

        // ── Approved Checkbox (Square) ──────────────────────────────────────
        // Create a square using merged cells
        const approvedCell = ws.getCell(startRow, startCol);
        ws.mergeCells(startRow, startCol, startRow + 3, startCol + 1);

        // Add thick border to create a visible square
        approvedCell.border = {
            top: { style: 'thick', color: { argb: 'FF000000' } },
            left: { style: 'thick', color: { argb: 'FF000000' } },
            bottom: { style: 'thick', color: { argb: 'FF000000' } },
            right: { style: 'thick', color: { argb: 'FF000000' } }
        };

        // Set background color to white
        approvedCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' }
        };

        // Center alignment
        approvedCell.alignment = {
            horizontal: 'center',
            vertical: 'middle'
        };

        // Optionally pre-fill with X if approved (remove this if you want it empty)
        if (project?.approvalStatus === 'approved') {
            approvedCell.value = 'X';
            approvedCell.font = {
                name: 'Arial',
                size: 20,
                bold: true,
                color: { argb: 'FF00AA00' }
            };
        } else {
            approvedCell.value = '';
        }

        // Approved label
        const approvedLabel = ws.getCell(startRow + 4, startCol);
        ws.mergeCells(startRow + 4, startCol, startRow + 4, startCol + 1);
        approvedLabel.value = 'APPROVED';
        approvedLabel.font = {
            name: 'Arial',
            size: 10,
            bold: true
        };
        approvedLabel.alignment = {
            horizontal: 'center',
            vertical: 'middle'
        };

        // ── Rejected Checkbox (Square) ──────────────────────────────────────
        const rejectedCell = ws.getCell(startRow, startCol + 3);
        ws.mergeCells(startRow, startCol + 3, startRow + 3, startCol + 4);

        // Add thick border to create a visible square
        rejectedCell.border = {
            top: { style: 'thick', color: { argb: 'FF000000' } },
            left: { style: 'thick', color: { argb: 'FF000000' } },
            bottom: { style: 'thick', color: { argb: 'FF000000' } },
            right: { style: 'thick', color: { argb: 'FF000000' } }
        };

        // Set background color to white
        rejectedCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' }
        };

        // Center alignment
        rejectedCell.alignment = {
            horizontal: 'center',
            vertical: 'middle'
        };

        // Optionally pre-fill with X if rejected
        if (project?.approvalStatus === 'rejected') {
            rejectedCell.value = 'X';
            rejectedCell.font = {
                name: 'Arial',
                size: 20,
                bold: true,
                color: { argb: 'FFFF0000' }
            };
        } else {
            rejectedCell.value = '';
        }

        // Rejected label
        const rejectedLabel = ws.getCell(startRow + 4, startCol + 3);
        ws.mergeCells(startRow + 4, startCol + 3, startRow + 4, startCol + 4);
        rejectedLabel.value = 'REJECTED';
        rejectedLabel.font = {
            name: 'Arial',
            size: 10,
            bold: true
        };
        rejectedLabel.alignment = {
            horizontal: 'center',
            vertical: 'middle'
        };

    } catch (error) {
        console.warn('Could not add approval/rejection checkboxes:', error);
    }
};

/**
 * Alternative: Add checkboxes using shapes (if ExcelJS supports it)
 * This creates filled squares using cell formatting
 */
const addCheckboxesWithShapes = (ws) => {
    try {
        const startRow = 2;
        const startCol = 11; // Column K

        // ── Approved Checkbox ──────────────────────────────────────────────
        // Create a larger square
        const approvedCell = ws.getCell(startRow, startCol);
        ws.mergeCells(startRow, startCol, startRow + 2, startCol + 1);

        // Make it look like a checkbox with borders
        approvedCell.border = {
            top: { style: 'medium', color: { argb: 'FF000000' } },
            left: { style: 'medium', color: { argb: 'FF000000' } },
            bottom: { style: 'medium', color: { argb: 'FF000000' } },
            right: { style: 'medium', color: { argb: 'FF000000' } }
        };

        approvedCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' }
        };

        approvedCell.alignment = {
            horizontal: 'center',
            vertical: 'middle'
        };

        // Label
        const approvedLabel = ws.getCell(startRow + 3, startCol);
        ws.mergeCells(startRow + 3, startCol, startRow + 3, startCol + 1);
        approvedLabel.value = '✓ APPROVED';
        approvedLabel.font = {
            name: 'Arial',
            size: 9,
            bold: true
        };
        approvedLabel.alignment = {
            horizontal: 'center',
            vertical: 'middle'
        };

        // ── Rejected Checkbox ──────────────────────────────────────────────
        const rejectedCell = ws.getCell(startRow, startCol + 3);
        ws.mergeCells(startRow, startCol + 3, startRow + 2, startCol + 4);

        rejectedCell.border = {
            top: { style: 'medium', color: { argb: 'FF000000' } },
            left: { style: 'medium', color: { argb: 'FF000000' } },
            bottom: { style: 'medium', color: { argb: 'FF000000' } },
            right: { style: 'medium', color: { argb: 'FF000000' } }
        };

        rejectedCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' }
        };

        rejectedCell.alignment = {
            horizontal: 'center',
            vertical: 'middle'
        };

        const rejectedLabel = ws.getCell(startRow + 3, startCol + 3);
        ws.mergeCells(startRow + 3, startCol + 3, startRow + 3, startCol + 4);
        rejectedLabel.value = '✗ REJECTED';
        rejectedLabel.font = {
            name: 'Arial',
            size: 9,
            bold: true
        };
        rejectedLabel.alignment = {
            horizontal: 'center',
            vertical: 'middle'
        };

    } catch (error) {
        console.warn('Could not add checkbox shapes:', error);
    }
};

/**
 * Configures print settings for the worksheet
 * Ensures the document prints on a single page with no margins
 */
const configurePrintSettings = (ws) => {
    try {
        // Set print area to include all content (A1:I57 based on template)
        ws.pageSetup.printArea = 'A1:I57';

        // Fit to one page (width and height)
        ws.pageSetup.fitToPage = true;
        ws.pageSetup.fitToWidth = 1;
        ws.pageSetup.fitToHeight = 1;

        // Set all margins to 0 (no margins)
        ws.pageSetup.margins = {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            header: 0,
            footer: 0
        };

        // Optional: Set paper size to A4 (standard)
        ws.pageSetup.paperSize = 9; // 9 = A4

        // Optional: Center the content on the page when printing
        ws.pageSetup.horizontalCentered = true;
        ws.pageSetup.verticalCentered = true;

        // Optional: Set print quality
        ws.pageSetup.printQuality = 600;

        // Optional: Repeat rows at top if needed (not necessary for single page)
        // ws.pageSetup.printTitlesRow = '1:4';

    } catch (error) {
        console.warn('Could not configure print settings:', error);
    }
};

/**
 * Exports the briefing document using the pre-designed Excel template.
 */
export const exportBriefing = async ({ project, showNotification }) => {
    try {
        if (!project) {
            showNotification('Projeto não encontrado para gerar o briefing.', 'warning');
            return;
        }

        // ── 1. Load template ─────────────────────────────────────────────────
        const response = await fetch('/templates/FQ-17.xlsx');
        if (!response.ok) {
            throw new Error(
                `Falha ao carregar o modelo (status ${response.status}). ` +
                'Verifique se o arquivo FQ-17.xlsx está em public/templates/'
            );
        }

        const arrayBuffer = await response.arrayBuffer();
        const workbook = new ExcelJS.Workbook();

        await workbook.xlsx.load(arrayBuffer, {
            ignoreNodes: false
        });

        // ── 1.5 Add Logo ──────────────────────────────────────────────────────
        const logoImageId = await loadLogo(workbook);

        const ws = workbook.getWorksheet('Planilha1') || workbook.worksheets[0];
        const briefing = project.briefing || {};

        // Add logo to the worksheet
        if (logoImageId != null) {
            ws.addImage(logoImageId, {
                tl: { col: 0.2, row: 0.2 },
                ext: { width: 150, height: 45 },
                editAs: 'oneCell'
            });
        }

        // ── FIX: Center the FQ-17 header and Rev. 01 ────────────────────────
        // Cell H1 contains "FQ-17" (and I1 likely merged with it)
        const fqCell = ws.getCell('H1');
        if (fqCell) {
            fqCell.alignment = { horizontal: 'center', vertical: 'middle' };
        }

        // Cell H2 contains "Rev. 01" (and I2 likely merged with it)
        const revCell = ws.getCell('H2');
        if (revCell) {
            revCell.alignment = { horizontal: 'center', vertical: 'middle' };
        }

        // ── 1.6 Configure Print Settings ─────────────────────────────────────
        // Apply print settings to ensure single page with no margins
        configurePrintSettings(ws);

        // ── 1.7 Add Approval/Rejection Checkboxes (for printing) ─────────────
        // Choose one of these methods:
        addApprovalRejectionCheckboxes(ws, project);
        // OR
        // addCheckboxesWithShapes(ws);

        // ── 2. Helpers ───────────────────────────────────────────────────────

        /**
         * Write "x" (bold, size 12, centered) into a tick cell when selected,
         * or clear it when not selected.
         */
        const setTick = (cellRef, isSelected) => {
            const cell = ws.getCell(cellRef);
            if (isSelected) {
                cell.value = 'x';
                cell.font = { name: 'Aptos Narrow', size: 12, bold: false };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else {
                cell.value = null;
            }
        };

        /**
         * Write a plain text value into a cell with left alignment.
         */
        const setText = (cellRef, value) => {
            const cell = ws.getCell(cellRef);
            cell.value = (value !== undefined && value !== null && value !== '') ? value : null;
            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        };

        /**
         * Write a text value into a cell without changing its alignment.
         */
        const setTextKeepAlignment = (cellRef, value) => {
            const cell = ws.getCell(cellRef);
            cell.value = (value !== undefined && value !== null && value !== '') ? value : null;
        };

        // ── 3. General Information ───────────────────────────────────────────
        // Row 3: A3:E3 merged (label "Data:" in A3)
        setTextKeepAlignment('A3', `Data: ${new Date().toLocaleDateString('pt-BR')}`);

        // Row 3: F3:I3 merged (label "Sample Request No:")
        setTextKeepAlignment('F3', `Sample Request No: ${project.code || ''}`);

        // Row 4: B4:I4 merged → Brand/Client Name value
        setText('B4', `${briefing.brandName || ''}/${project.clientName || ''}`);

        // ── 4. Product Type (rows 5-7) ───────────────────────────────────────
        const productType = briefing.productType || '';
        setTick('C5', productType === 'Shampoo');
        setTick('E5', productType === 'Conditioner');
        setTick('G5', productType === 'Hair Mask');
        setTick('I5', productType === 'Straightening');

        setTick('C6', productType === 'Leave-in');
        setTick('E6', productType === 'Oil/Serum');
        setTick('G6', productType === 'Spray');
        setTick('I6', productType === 'Treatment kit');

        // Row 7: C7:I7 is merged
        const isProductTypeOthers = productType === 'Others';
        setText('C7', isProductTypeOthers ? (briefing.otherProductType || '') : null);

        // ── 5. Target Audience (rows 9-10) ──────────────────────────────────
        const targetAudience = briefing.targetAudience || '';
        setTick('C9', targetAudience === 'Female');
        setTick('E9', targetAudience === 'Male');
        setTick('G9', targetAudience === 'Unisex');
        setTick('I9', targetAudience === 'Baby (0-3)');
        setTick('C10', targetAudience === 'Kids (3-12)');
        setTick('E10', targetAudience === 'Adolescent');

        // ── 6. Competitors (rows 12-13) ──────────────────────────────────────
        // A12:I13 is merged
        setTextKeepAlignment('A12', `Competitors:  ${briefing.competitors || ''}`);

        // ── 7. Physical Form (row 15) ────────────────────────────────────────
        const physicalForm = briefing.physicalForm || '';
        setTick('C15', physicalForm === 'Liquid');
        setTick('E15', physicalForm === 'Creamy');
        setTick('G15', physicalForm === 'Oily');
        setTick('I15', physicalForm === 'Powder');

        // ── 8. Viscosity (row 16) ────────────────────────────────────────────
        const viscosity = briefing.viscosity || '';
        setTick('C16', viscosity === 'Fluid');
        setTick('E16', viscosity === 'Medium');
        setTick('G16', viscosity === 'Thick');

        // ── 9. Fragrance (rows 17-19) ────────────────────────────────────────
        const fragrance = briefing.fragrance || '';
        setTick('C17', fragrance === 'Floral');
        setTick('E17', fragrance === 'Woody');
        setTick('G17', fragrance === 'Herbal');
        setTick('I17', fragrance === 'Fresh');
        setTick('C18', fragrance === 'Sweet');
        setTick('E18', fragrance === 'Fruity');
        setTick('G18', fragrance === 'Citrus');
        setTick('I18', fragrance === 'Other');

        // Row 19: A19:H19 is merged
        const isFragranceOther = fragrance === 'Other';
        setText('A19', isFragranceOther ? (briefing.otherFragrance || null) : null);

        // ── 10. Required Compliance (rows 20-22) ─────────────────────────────
        const requiredCompliance = briefing.requiredCompliance || '';
        setTick('C20', requiredCompliance === 'ANVISA');
        setTick('E20', requiredCompliance === 'FDA');
        setTick('G20', requiredCompliance === 'CPNP');
        setTick('I20', requiredCompliance === 'SFDA');

        // Row 22: C22:I22 is merged
        const isComplianceOthers = requiredCompliance === 'Others';
        setText('C22', isComplianceOthers ? (briefing.otherCompliance || null) : null);

        // ── 11. Key Claims (rows 24-26) ──────────────────────────────────────
        const keyClaims = briefing.keyClaims || [];
        setTick('C24', keyClaims.includes('Sulfate Free'));
        setTick('E24', keyClaims.includes('Formaldhyde Free') || keyClaims.includes('Formaldehyde Free'));
        setTick('G24', keyClaims.includes('Paraben Free'));
        setTick('I24', keyClaims.includes('Cruelty Free'));

        setTick('C25', keyClaims.includes('Vegan'));
        setTick('E25', keyClaims.includes('Organic'));
        setTick('G25', keyClaims.includes('Alergenic Free') || keyClaims.includes('Allergenic Free'));
        setTick('I25', keyClaims.includes('Halal'));

        setTick('C26', keyClaims.includes('Silicone Free'));

        // Row 26: E26:I26 is merged
        const isClaimsOthers = keyClaims.includes('Other') || keyClaims.includes('Others');
        setText('E26', isClaimsOthers ? (briefing.otherKeyClaims || null) : null);

        // ── 12. Product Function (rows 28-34) ────────────────────────────────
        const productFunctions = briefing.productFunctions || [];
        for (let i = 0; i < 7; i++) {
            setText(`A${28 + i}`, productFunctions[i] ? `${i + 1}. ${productFunctions[i]}` : null);
        }

        // ── 13. Delivery Timelines ────────────────────────────────────────────
        if (briefing.sampleDeliveryDate) {
            setTextKeepAlignment('A36', `Sample Delivery Date: ${new Date(briefing.sampleDeliveryDate).toLocaleDateString('pt-BR')}`);
        }
        if (briefing.finalProductDeliveryDate) {
            setTextKeepAlignment('A38', `Final Product Delivery Date: ${new Date(briefing.finalProductDeliveryDate).toLocaleDateString('pt-BR')}`);
        }

        // ── 14. Additional Information (rows 41-46) ───────────────────────────
        const additionalInfo = briefing.additionalInfo || [];
        for (let i = 0; i < 6; i++) {
            setText(`A${41 + i}`, additionalInfo[i] || null);
        }

        // ── 15. Signature dates (row 57) ─────────────────────────────────────
        const dateStr = `Data: 24/07/2025`;
        setTextKeepAlignment('A57', dateStr);
        setTextKeepAlignment('C57', dateStr);
        setTextKeepAlignment('G57', dateStr);

        // ── 16. Save ─────────────────────────────────────────────────────────
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        saveAs(blob, `Briefing_${project.code || 'PROJ'}_${Date.now()}.xlsx`);

        showNotification(
            `Briefing gerado com sucesso para o projeto ${project.projectName}!`,
            'success'
        );

    } catch (error) {
        console.error('Erro ao gerar briefing:', error);
        showNotification(`Erro ao gerar briefing: ${error.message}`, 'error');
    }
};